import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SelectBox } from 'cbc-shared-lib';
import { CodeConversionMstDto, Sgke903aResponseCompanyAreaInfo, Sgke903aResponseInitialhyojiInfo } from 'sg-api';
import { ListSearchUtils } from '../../../../../sg-ke-shared/util/list-search-utils';

@Component({
  selector: 'app-sgke310d-condition',
  templateUrl: './sgke310d-condition.component.html',
  styleUrl: './sgke310d-condition.component.scss'
})
export class Sgke310dConditionComponent implements OnInit, OnDestroy {

  /** 会社地区情報 */
  @Input() cmpnyAreaList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** 初期表示用会社地区情報 */
  @Input() cmpnyAreaInit: Sgke903aResponseInitialhyojiInfo;
  /** コードマスタ取得 */
  @Input() codeConversionMstMap: Map<string, Array<CodeConversionMstDto>> = null;

  /** クリアボタン押下イベント */
  @Output() onClickClearEvent = new EventEmitter();
  /** 検索ボタン押下イベント */
  @Output() onClickSearchEvent = new EventEmitter();

  /** 検索条件フォーム */
  searchConditionForm: UntypedFormGroup;

  /** 督促管理区分コードリスト(S300090) */
  tokusokuKubunList: SelectBox[] = [];
  /** 会社コードリスト(S900020) */
  companycodeList: SelectBox[] = [];
  /** 地区（営業）コードリスト(S900030) */
  areaTradeCodeList: SelectBox[] = [];
  /** 地区（供給地点）リスト(S900030) */
  areaSuppntCodeList: SelectBox[] = [];

  /** 親FormGroup */
  parentForm: UntypedFormGroup;

  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {UntypedFormBuilder} fb
   * @param {ChangeDetectorRef} cd
   * @memberof Sgke310dConditionComponent
   */
  constructor(
    private parent: FormGroupDirective,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    public listUtils: ListSearchUtils,
  ) {
  }

  /**
   * 初期処理
   * @memberof Sgke310dConditionComponent
   */
  ngOnInit() {
    // セレクターに値をセットする
    this.companycodeList.push(...this.getSelector('companycode', 'companycodeAname',this.cmpnyAreaList));
    this.areaTradeCodeList.push(...this.getSelector('areacode', 'areacodeAname',this.cmpnyAreaList));
    this.areaSuppntCodeList.push(...this.getSelector('areacode', 'areacodeAname',this.cmpnyAreaList));

    const companycode = this.listUtils.getListboxValue(this.cmpnyAreaInit.companycode, this.companycodeList);
    const areaSuppntCode = this.listUtils.getListboxValue(this.cmpnyAreaInit.areacode, this.areaSuppntCodeList);

    /** FormGroup */
    this.searchConditionForm = this.fb.group({
      pressmanagementdivisionCodeList: this.fb.nonNullable.control({ value: ["1", "2",], disabled: false }, Validators.required),
      areaTradeCode: this.fb.nonNullable.control<string>({ value: null, disabled: false }),
      companycode: this.fb.nonNullable.control<string>({ value: companycode, disabled: false }),
      areaSuppntCode: this.fb.nonNullable.control({ value: areaSuppntCode, disabled: false }, Validators.required),
      alldateHyojiFlg: this.fb.nonNullable.control({ value: true, disabled: false }, Validators.required),
    });

    // FormGroupの登録
    this.parentForm = this.parent.form;
    // 親コンポーネントのformに検索条件formを追加
    this.parentForm.addControl("cndt", this.searchConditionForm);

    // 対象プロパティが変更された場合のみ処理する
    if (this.codeConversionMstMap) {
      // コード値毎のkeyに対して、userCode(セレクトボックスの値)、hyojiCont(セレクトボックスの表示文字)がサービスから設定されているので、それを取得する
      this.tokusokuKubunList.push(...this.getSelectBoxFromCodeConversionMst('S300090'));
    }
    this.cd.detectChanges();
  }

  /**
   * 購読解除
   * @memberof Sgke310dConditionComponent
   */
  ngOnDestroy() {
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
    this.parentForm.removeControl('cndt');
  }

  /**
   * SGKE903Aレスポンスからセレクターにオプションをセット
   * @param {string} value
   * @param {string} viewValue
   * @return {*}
   * @memberof Sgke310dConditionComponent
   */
  getSelector(value: string, viewValue:string, companyAreaInfoList: Array<Sgke903aResponseCompanyAreaInfo>): SelectBox[]{

    // SGKE903Aレスポンスのコードとコード値をまとめてリスト化
    let retSelectBoxList: SelectBox[] = [];
    companyAreaInfoList.map(data => {
      const select: SelectBox = { value: data[value], viewValue: data[viewValue] };
      retSelectBoxList.push(select);
    });

    // 重複を削除しソート
    const uniqueSortedArray = Array.from(
      new Map(retSelectBoxList.map(item => [item.value, item])).values()
    ).sort((a, b) => a.value > b.value ? 1 : -1);

    return uniqueSortedArray;
  }

  /**
   * UntypedFormControl取得
   * @param {*} ctrlName
   * @return {*}  {UntypedFormControl}
   * @memberof Sgke310dConditionComponent
   */
  getControl(ctrlName): UntypedFormControl {
    return this.searchConditionForm.get(ctrlName) as UntypedFormControl;
  }
  /**
   * クリアボタン押下時処理
   * @memberof Sgke310dConditionComponent
   */
  onClickClear(): void {
    this.searchConditionForm.reset();
    this.onClickClearEvent.emit();
    const chikuCtl = this.getControl('companycode') as UntypedFormControl;
    this.onChangeCompany(chikuCtl.getRawValue());
  }

  /**
   * 会社ドロップダウン変化時処理
   * @param {string} companycode
   * @memberof Sgke310dConditionComponent
   */
  onChangeCompany(companycode: string) {
    // 会社コードリストの値が変わった場合
    const chikuCtl = this.getControl('areacode') as UntypedFormControl;
    chikuCtl?.setValue(null);
    if (companycode) {
      const newAreaList: Array<Sgke903aResponseCompanyAreaInfo> = this.cmpnyAreaList
        .filter(list => list.companycode == companycode);
      this.areaTradeCodeList = this.getSelector('areacode', 'areacodeAname', newAreaList);
    } else {
      this.areaTradeCodeList = this.getSelector('areacode', 'areacodeAname', this.cmpnyAreaList);
    }
  }

  /**
  * コードIDに対応するコード変換マスタ値をSelectBoxの配列で戻す
   * @param {string} codeId
   * @return {*}  {SelectBox[]}
   * @memberof Sgke310dConditionComponent
   */
  getSelectBoxFromCodeConversionMst(codeId: string): SelectBox[] {
    let retSelectBoxList: SelectBox[] = [];
    let codeList: CodeConversionMstDto[] = [];
    if (this.codeConversionMstMap.has(codeId)) {
      codeList = this.codeConversionMstMap.get(codeId);
      if (codeList) {
        codeList.forEach(data => {
          const select: SelectBox = { value: data.userCode, viewValue: data.hyojiCont };
          retSelectBoxList.push(select);
        })
      }
    }
    return retSelectBoxList;
  }

}
